import {
  trigger,
  transition,
  animate,
  style,
  state,
} from '@angular/animations';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { UserService } from 'src/app/core/api/user/user.service';
import { AuthenticatedUserService } from 'src/app/core/services/authenticated-user/authenticated-user.service';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { Region } from 'src/app/shared/models/dtos/region/region';
import { specialCharValidator } from 'src/app/shared/validators/registration-validators';
import { Roles } from 'src/app/utils/roles';
import { RoutesKeys } from 'src/app/utils/routes';
import { translocoLang } from 'src/app/utils/translocoLang';

type TOnboardingFormGroup = FormGroup<{
  language: FormControl<string | null>;
  picture: FormControl<string | null>;
  name: FormControl<string | null>;
  email: FormControl<string | null>;
  region: FormControl<Region | null>;
}>;

@Component({
  selector: 'app-onboarding',
  templateUrl: './onboarding.component.html',
  styleUrls: ['./onboarding.component.scss'],
  animations: [
    trigger('fade', [
      state(
        'void',
        style({
          opacity: 0,
        })
      ),
      transition(':enter', [
        animate(
          '250ms ease-in',
          style({
            opacity: 1,
          })
        ),
      ]),
    ]),
    trigger('fadeLoading', [
      transition(':leave', [
        animate(
          '250ms ease-out',
          style({
            opacity: 0,
          })
        ),
      ]),
    ]),
  ],
})

/**
 * Onboard organism
 */
export class OnboardingComponent implements OnInit, OnDestroy {
  onboardingFormGroup!: TOnboardingFormGroup;
  private subsink = new Subscription();
  regions?: Region[];
  showLoading = true;
  loading = true;
  loadingSave = false;
  role?: string;
  rolesReference = Roles;
  onboardingLabels = {
    accountRegion: 'account-region',
    selectRegion: 'select-region',
    selectARegion: 'select-a-region',
    onboarding: 'onboarding',
    onboardingDetail: 'onboarding-detailed-info-1',
    onboardingDetail2: 'onboarding-detailed-info-2',
    next: 'next-button',
  };

  /**
   * @constructor
   */
  constructor(
    private userService: UserService,
    private translationService: TranslationService,
    private snackbarService: SnackBarService,
    private router: Router,
    private readonly authenticatedUserService: AuthenticatedUserService
  ) {}

  /**
   * Get regions to select and start the whole form
   */
  ngOnInit() {
    this.subsink.add(
      this.userService.getRegions().subscribe((regions) => {
        this.regions = regions;
      })
    );

    this.onboardingFormGroup = new FormGroup({
      language: new FormControl<string | null>(null),
      picture: new FormControl<string | null>(null),
      name: new FormControl<string | null>(null, [
        Validators.minLength(3),
        Validators.maxLength(255),
        specialCharValidator(),
      ]),
      email: new FormControl<string | null>(null),
      region: new FormControl<Region | null>(null),
    });

    this.userService.getAdminInfo().subscribe((adminInfo) => {
      this.role = adminInfo.role;
      this.showLoading = false;
      if (this.role !== Roles.GLOBAL_ADMIN) {
        this.onboardingFormGroup
          .get('region')
          ?.setValidators([Validators.required]);
      }
      this.onboardingFormGroup.setValue({
        language: this.getCurrentLanguage(localStorage.getItem(translocoLang)!),
        picture: adminInfo.picture || null,
        name: adminInfo.name || null,
        email: adminInfo.email || null,
        region: null,
      });
      this.translationService.translateObject(this.onboardingLabels);
    });
  }

  /**
   * Check current language
   * @param {string} language
   * @return {string}
   */
  getCurrentLanguage(language: string) {
    return language === 'pt-BR' ? 'pt' : language;
  }

  /**
   * @param {Region} region region to extract name from
   * @return {string} extracted region name
   */
  regionName(region: Region): string {
    return region.name;
  }

  /**
   * A shortcut for region form control
   * @return {FormControl}
   */
  get regionFormControl() {
    return this.onboardingFormGroup.controls['region'];
  }

  /**
   * Save changes after pressing the button
   */
  saveAccountChanges() {
    this.loadingSave = true;
    this.subsink.add(
      this.authenticatedUserService
        .updateOnboardingData(
          this.onboardingFormGroup.value.region?.id!,
          this.onboardingFormGroup.value.language as any,
          this.onboardingFormGroup.value.name!,
          this.onboardingFormGroup.value.picture!
        )
        .subscribe({
          next: () => {
            this.translationService
              .translateString('success')
              .then((string) => this.snackbarService.success(string));
          },
          error: () => {
            this.loadingSave = false;
            this.translationService
              .translateString('error')
              .then((string) => this.snackbarService.error(string));
          },
          complete: () => {
            this.router.navigate([RoutesKeys.HOME]);
          },
        })
    );
  }

  /**
   * Clean up resources
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }
}
